import { computed, readonly, ref } from "vue";

interface ForbiddenAttempt {
	isCurrent: () => boolean;
	refresh: () => Promise<"dialog" | "settled">;
	showDialog: (isCurrent: () => boolean, ownDialog: (dismiss: () => void) => void) => Promise<void>;
	onError: (error: unknown) => void;
}

interface ForbiddenOperation {
	refresh: Promise<void>;
	dismissDialog?: () => void;
}

/**
 * One 403 operation: idle -> refetching -> dialog-open -> idle (settled).
 * A confirmed grant or unavailable verdict settles directly after refetching;
 * only a confirmed loss of write access needs a revocation dialog.
 * Permission state belongs to the verdict, never to this dialog lifecycle.
 * Concurrent 403s join the same refresh/dialog. Every success, rejection or stale
 * continuation settles its own attempt. cancel() settles immediately on project/org
 * changes, unmount or a newer access request; old completions cannot clear new work.
 */
export function useProjectForbiddenFlow() {
	const state = ref<"idle" | "refetching" | "dialog-open">("idle");
	let active: ForbiddenOperation | undefined;

	function cancel(): void {
		const operation = active;
		const dialogOpen = state.value === "dialog-open";
		// Invalidate first: dismissing resolves the prompt and must not honor its continuation.
		active = undefined;
		state.value = "idle";
		if (dialogOpen) operation?.dismissDialog?.();
	}

	function handle(attempt: ForbiddenAttempt): Promise<void> {
		if (active) return active.refresh;
		const operation: ForbiddenOperation = { refresh: Promise.resolve() };
		active = operation;
		state.value = "refetching";
		const isCurrent = () => active === operation && attempt.isCurrent();
		const settle = () => {
			if (active === operation) cancel();
		};
		operation.refresh = (async () => {
			try {
				const outcome = await attempt.refresh();
				if (outcome === "settled") {
					settle();
					return;
				}
				if (!isCurrent()) {
					settle();
					return;
				}
				state.value = "dialog-open";
				// API error listeners wait for the verdict, not for the user's dialog choice.
				// Dialog resolution/dismissal/rejection/logout failure all release this attempt.
				void Promise.resolve()
					.then(() => {
						if (isCurrent())
							return attempt.showDialog(isCurrent, (dismiss) => {
								operation.dismissDialog = dismiss;
								if (!isCurrent()) dismiss();
							});
					})
					.catch(attempt.onError)
					.finally(settle);
			} catch (error) {
				try {
					attempt.onError(error);
				} finally {
					settle();
				}
			}
		})();
		return operation.refresh;
	}

	return { state: readonly(state), handling: computed(() => state.value !== "idle"), handle, cancel };
}
